/**
 * How a work package on a Gantt mission is billed.
 *
 * A package (an action) can carry a billing type. It covers the package's tasks and every
 * package under it, unless a sub-package sets its own; a fixed fee covers everything under
 * it, whatever a sub-package says. A package with no type anywhere above it is "Not set"
 * ("As the mission bills") and bills exactly as before: time and materials is the mission's
 * revenue model, never a package's. The use cases, with their numbers, are
 * qa/scenarios/budget-extended.md.
 *
 * - Fixed fee, by milestones (a lump sum): the fundable milestones in it are the fee: their
 *   sum is the package's revenue, each bills once ready to invoice.
 * - Fixed fee, by % complete: the fee is the package's revenue; it bills fee x confirmed %,
 *   less what's billed. Each confirmation books its change in % on its "as of" date.
 * - Cost plus: time at its cost rate and cost lines at cost, each plus the markup;
 *   fundable milestones in it bill too (a fixed fee on top).
 *
 * A package's revenue has one source per model: the items below it (or an override typed on
 * it) with no type; the work for cost plus; the milestones' sum for a
 * fixed fee by milestones; the fee typed on it for a fixed fee by % complete. A revenue typed
 * on a package whose model calculates it is deleted on write (getBillingTypeChange, the API's
 * clearPricedRevenue) and ignored on read (the engines).
 *
 * The fields live on the action: billingType, billingMarkup (a percent), billingProgress
 * ([{ pct, asOf, at, by }], appended by the API), and the fee by % complete is estRevenue.
 * The API checks the same rules in api/action/action.billing.js.
 */

import { eachDayOfInterval, endOfDay, isWeekend, startOfDay } from "date-fns"
import { fromZonedTime, toZonedTime } from "date-fns-tz"

// utils/dates' getTimeUtc, kept here: the timesheet workers load this file (through mf-utils),
// and utils/dates pulls in utils/utils, whose React hooks crash a worker in dev ($RefreshSig$).
const getTimeUtc = (date) => {
    try {
        return fromZonedTime(date).getTime()
    } catch (err) {
        return undefined
    }
}

export const BILLING = {
    FIXED_MILESTONES: "fixed-milestones",
    FIXED_PERCENT: "fixed-percent",
    COST_PLUS: "cost-plus",
}

export const BILLING_TYPES = Object.values(BILLING)

/** The most a markup can be, in percent. */
export const MAX_MARKUP = 1000

const DAY_MS = 24 * 60 * 60 * 1000

const MILESTONE_META_TYPE = 4

const num = (value) => {
    const n = Number(value)

    return Number.isFinite(n) ? n : 0
}

const cents = (value) => Math.round(value * 100) / 100

export const isFixedFee = (type) => type === BILLING.FIXED_MILESTONES || type === BILLING.FIXED_PERCENT

export const isBillingType = (type) => BILLING_TYPES.includes(type)

/** The package and the packages above it, nearest first. Stops at a loop or a missing parent. */
function getChain(actionId, allActions = []) {
    const byId = new Map(allActions.map((a) => [a?._id, a]))
    const chain = []
    const seen = new Set()
    let current = byId.get(actionId)

    while (current && !seen.has(current._id)) {
        seen.add(current._id)
        chain.push(current)
        current = current.parentId ? byId.get(current.parentId) : null
    }

    return chain
}

/** Every package under this one, in the order of allActions. */
function getDescendants(actionId, allActions = []) {
    const ids = new Set([actionId])
    let grew = true

    // A package can sit anywhere in the list, before or after its parent
    while (grew) {
        grew = false
        allActions.forEach((a) => {
            if (a?.parentId && ids.has(a.parentId) && !ids.has(a._id)) {
                ids.add(a._id)
                grew = true
            }
        })
    }

    return allActions.filter((a) => a && a._id !== actionId && ids.has(a._id))
}

/**
 * A fixed fee's fee: by % complete, the fee typed on the package (its estRevenue); by
 * milestones, the sum of its fundable milestones (its sub-packages' too, each counted once).
 * null for the other models. API twin: financials.engine.js#packageFee.
 */
export function getPackageFee({ action, allActions = [] }) {
    if (action?.billingType === BILLING.FIXED_PERCENT) return num(action.estRevenue)
    if (action?.billingType !== BILLING.FIXED_MILESTONES) return null

    return cents(
        getPackageMilestones({ actionId: action._id, allActions }).reduce((sum, ms) => sum + num(ms.billRate), 0),
    )
}

function describeBilling(owner, allActions) {
    return {
        type: owner.billingType,
        packageId: owner._id,
        markup: num(owner.billingMarkup),
        fee: getPackageFee({ action: owner, allActions }),
        progress: owner.billingProgress || [],
    }
}

/**
 * The billing that applies to a package: the topmost fixed fee above it (or its own), else
 * the nearest type set on it or above it. null is Not set.
 *
 * @param input.actionId {String}
 * @param input.allActions {Object[]} Every action of the mission
 * @return {null|{type: String, packageId: String, markup: Number, fee: (Number|null), progress: Object[]}}
 */
export function getPackageBilling({ actionId, allActions }) {
    const chain = getChain(actionId, allActions)
    const owner =
        [...chain].reverse().find((a) => isFixedFee(a.billingType)) || chain.find((a) => isBillingType(a.billingType))

    return owner ? describeBilling(owner, allActions) : null
}

/** A task's billing is its package's. */
export function getTaskBilling({ ai, allActions }) {
    return ai?.actionId ? getPackageBilling({ actionId: ai.actionId, allActions }) : null
}

/**
 * A fixed fee above a package covers it: it can't bill its own way.
 *
 * @return {null|{packageId: String}} The topmost fixed-fee package above, if any
 */
export function getBillingLock({ actionId, allActions }) {
    const above = getChain(actionId, allActions).slice(1)
    const fixed = [...above].reverse().find((a) => isFixedFee(a.billingType))

    return fixed ? { packageId: fixed._id } : null
}

/** The packages under this one that bill their own way (a fixed fee here would cover them). */
export function getTypedPackagesBelow({ actionId, allActions }) {
    return getDescendants(actionId, allActions).filter((a) => isBillingType(a.billingType))
}

/** Cost plus the markup, in percent. */
export function costPlus(cost, markup) {
    return (num(cost) * (100 + num(markup))) / 100
}

/** A task's hours when nobody estimated them: its working days at 8 hours. */
function workingHours(ai) {
    if (!ai?.startDate || !ai?.endDate) return 0

    try {
        return (
            eachDayOfInterval({ start: toZonedTime(ai.startDate), end: toZonedTime(ai.endDate) }).filter(
                (d) => !isWeekend(d),
            ).length * 8
        )
    } catch (e) {
        return 0
    }
}

/**
 * The % complete a package's tasks add up to: each task's % weighted by its hours, the
 * tasks of the packages under it included. Milestones are markers, not work.
 *
 * @param input.hoursOf {Function} A task's estimated hours (default: working days x 8)
 * @return {Number} A whole percent
 */
export function getSuggestedPercent({ actionId, allActions, hoursOf = workingHours }) {
    const packages = [...getChain(actionId, allActions).slice(0, 1), ...getDescendants(actionId, allActions)]
    let total = 0
    let done = 0

    packages.forEach((pkg) => {
        ;(pkg.actionItems || []).forEach((ai) => {
            if (ai?.metaType === MILESTONE_META_TYPE) return

            const hours = num(hoursOf(ai)) || workingHours(ai)

            total += hours
            done += (hours * Math.min(100, Math.max(0, num(ai.percentComplete)))) / 100
        })
    })

    return total ? Math.round((done / total) * 100) : 0
}

/** Confirmations in the order they count: by "as of" date, then by when they were made. */
function orderProgress(progress = []) {
    return progress
        .filter((p) => Number.isFinite(p?.pct) && Number.isFinite(p?.asOf))
        .slice()
        .sort((a, b) => a.asOf - b.asOf || num(a.at) - num(b.at))
}

/** The % confirmed last, 0 when nothing is. */
export function getConfirmedPercent(progress) {
    const ordered = orderProgress(progress)

    return ordered.length ? ordered[ordered.length - 1].pct : 0
}

/**
 * Each confirmation and the revenue it books: the fee times its change in %.
 *
 * @return {{asOf: Number, pct: Number, amount: Number}[]}
 */
export function getProgressSteps({ fee, progress }) {
    let previous = 0

    return orderProgress(progress).map((entry) => {
        const amount = (num(fee) * (entry.pct - previous)) / 100

        previous = entry.pct

        return { asOf: entry.asOf, pct: entry.pct, amount }
    })
}

/** The revenue confirmations booked in a period (either end optional). */
export function getProgressRevenue({ fee, progress, from, to }) {
    const start = from === undefined || from === null ? -Infinity : +from
    const end = to === undefined || to === null ? Infinity : +to

    return getProgressSteps({ fee, progress })
        .filter((step) => step.asOf >= start && step.asOf <= end)
        .reduce((sum, step) => sum + step.amount, 0)
}

/**
 * What a package billed by % complete has earned against what's billed.
 *
 * @param input.billed {Number} What its progress rows are already invoiced for
 * @return {{confirmedPct: Number, earned: Number, billed: Number, toBill: Number, billedAhead: Number}}
 */
export function getProgressToBill({ fee, progress, billed }) {
    const confirmedPct = getConfirmedPercent(progress)
    const earned = (num(fee) * confirmedPct) / 100
    const invoiced = num(billed)

    return {
        confirmedPct,
        earned,
        billed: invoiced,
        toBill: Math.max(0, cents(earned - invoiced)),
        billedAhead: Math.max(0, cents(invoiced - earned)),
    }
}

const fail = (code, message, extra = {}) => ({ code, message, ...extra })

const isGantt = (mission) => mission?.projectType === "mx-gantt"

const titleOf = (actionId, allActions = []) => allActions.find((a) => a?._id === actionId)?.title || "the package above"

/** "Kitchen's", "Groundworks'". */
export const possessive = (name) => (/s$/i.test(name || "") ? `${name}'` : `${name}'s`)

/**
 * Whether a change to a package's billing can be made. Changes that don't touch billing
 * pass.
 *
 * @param input.change {Object} The fields being written
 * @param input.action {Object} The package as it is
 * @param input.allActions {Object[]} Every action of the mission
 * @param input.mission {Object}
 * @return {null|{code: String, message: String}}
 */
export function validateBillingChange({ change = {}, action, allActions = [], mission }) {
    const settingType = "billingType" in change && change.billingType !== null && change.billingType !== undefined
    const settingMarkup =
        "billingMarkup" in change && change.billingMarkup !== null && change.billingMarkup !== undefined
    const type = "billingType" in change ? change.billingType : action?.billingType

    if ((settingType || settingMarkup) && !isGantt(mission)) {
        return fail("not-gantt", "Billing by work package is for Gantt missions.")
    }

    if (settingType) {
        if (!isBillingType(change.billingType)) {
            return fail("bad-type", "Pick Time & materials, Fixed fee (by milestones or by % complete) or Cost plus.")
        }

        const lock = getBillingLock({ actionId: action?._id, allActions })

        if (lock) {
            return fail("locked", `Covered by ${possessive(titleOf(lock.packageId, allActions))} fee.`, lock)
        }

        if (isFixedFee(change.billingType)) {
            const below = getTypedPackagesBelow({ actionId: action?._id, allActions })

            if (below.length) {
                return fail(
                    "typed-below",
                    `A fixed fee covers everything under it. Clear the billing on ${below
                        .map((a) => a.title || "a package")
                        .join(", ")} first.`,
                    { packageIds: below.map((a) => a._id) },
                )
            }
        }
    }

    if (settingMarkup) {
        const markup = change.billingMarkup

        if (typeof markup !== "number" || !Number.isFinite(markup) || markup < 0 || markup > MAX_MARKUP) {
            return fail("bad-markup", `The markup has to be between 0% and ${MAX_MARKUP}%.`)
        }
    }

    if ("estRevenue" in change && isFixedFee(type) && change.estRevenue !== null && change.estRevenue !== undefined) {
        const fee = change.estRevenue

        if (typeof fee !== "number" || !Number.isFinite(fee) || fee < 0) {
            return fail("bad-fee", "The fee can't be negative.")
        }
    }

    return null
}

/**
 * Whether a % can be confirmed on a package billed by % complete.
 *
 * @param input.entry {{pct: Number, asOf: Number}}
 * @param input.action {Object} The package, with its billingProgress
 * @param [input.now] {Number} A day's grace covers every timezone's "today"
 * @return {null|{code: String, message: String}}
 */
export function validateProgressEntry({ entry = {}, action, now = Date.now() }) {
    if (action?.billingType !== BILLING.FIXED_PERCENT) {
        return fail("not-percent", "Only a package billed by % complete takes a confirmed %.")
    }

    const { pct, asOf } = entry

    if (typeof pct !== "number" || !Number.isFinite(pct) || pct < 0 || pct > 100) {
        return fail("bad-pct", "The % has to be between 0 and 100.")
    }

    if (typeof asOf !== "number" || !Number.isFinite(asOf)) {
        return fail("bad-date", "Pick the date the % is as of.")
    }

    if (asOf > now + DAY_MS) {
        return fail("future", "The date can't be in the future.")
    }

    const ordered = orderProgress(action.billingProgress)
    const last = ordered[ordered.length - 1]

    if (last && asOf < last.asOf) {
        return fail("before-last", "The date can't be before the last confirmed %.")
    }

    return null
}

/** mission.expenseRebill.mode: what an approved expense on the mission bills the client. */
export const EXPENSE_REBILL = {
    OFF: "off",
    COST: "cost",
    MARKUP: "markup",
}

/** The markup in percent an approved expense bills at, or null when the mission doesn't rebill. */
export function getExpenseRebillMarkup(mission) {
    const rebill = mission?.expenseRebill

    if (rebill?.mode === EXPENSE_REBILL.COST) return 0
    if (rebill?.mode === EXPENSE_REBILL.MARKUP) return num(rebill.markup)

    return null
}

/**
 * What one approved expense rebills, in its own currency: its amount plus the markup, then
 * the tax and tip the org charges clients (expenseInvoiceOptions), at cost. API twin:
 * financials.engine.js.
 *
 * @return {{cost: Number, markupAmount: Number, tax: Number, tip: Number, billed: Number}}
 */
export function getExpenseRebillAmount({ expense, markup, orgData }) {
    const cost = num(expense?.amount)
    const charges = orgData?.expenseInvoiceOptions || []
    const tax = charges.includes("tax") ? num(expense?.tax1) + num(expense?.tax2) : 0
    const tip = charges.includes("tip") ? num(expense?.tip) : 0
    const marked = costPlus(cost, markup)

    return { cost, markupAmount: marked - cost, tax, tip, billed: marked + tax + tip }
}

/** Only approved (or paid) expenses are offered and count as revenue. */
export const isRebillableExpense = (expense) => expense?.status === "approved" || expense?.status === "paid"

export const BILLING_LABELS = {
    [BILLING.FIXED_MILESTONES]: "Fixed fee: by milestones",
    [BILLING.FIXED_PERCENT]: "Fixed fee: by % complete",
    [BILLING.COST_PLUS]: "Cost plus",
}

/**
 * What a package with no billing type of its own (and none above it) shows: its revenue comes
 * from the items below it, as before package billing.
 */
export const NO_BILLING_LABEL = "Inherits from children"

/** "Cost plus 15%", "Fixed fee: by milestones", "As the mission bills". */
export function getBillingLabel(billing) {
    if (!billing?.type) return NO_BILLING_LABEL

    return billing.type === BILLING.COST_PLUS
        ? `Cost plus ${cents(num(billing.markup))}%`
        : BILLING_LABELS[billing.type] || NO_BILLING_LABEL
}

/** What each billing type bills, in a line (the cell's tooltip). */
export const BILLING_HINTS = {
    [BILLING.FIXED_MILESTONES]:
        "The fundable milestones in it are the fee: their sum is the revenue, each bills once ready to invoice. Time and lines are cost only.",
    [BILLING.FIXED_PERCENT]:
        "The fee is the revenue. It bills fee x the % you confirm, less what's billed. Time, lines and milestones are cost only.",
    [BILLING.COST_PLUS]:
        "Approved time at its cost rate and cost lines at cost, plus the markup. Fundable milestones in it bill on top.",
}

/** Every task (milestones included) of the package and the packages under it. */
export function getPackageTasks({ actionId, allActions }) {
    return [...getChain(actionId, allActions).slice(0, 1), ...getDescendants(actionId, allActions)].flatMap(
        (pkg) => pkg.actionItems || [],
    )
}

/** The fundable milestones of the package and the packages under it. */
export function getPackageMilestones({ actionId, allActions }) {
    return getPackageTasks({ actionId, allActions }).filter(isFundableMilestone)
}

const isSetAmount = (v) => v !== null && v !== undefined && v !== ""

const isFundableMilestone = (ai) => ai?.metaType === MILESTONE_META_TYPE && ai.pinType === "fundable-milestone"

/**
 * What needs the PM's attention on a package that bills its own way:
 *  - no-fee: by % complete, no fee set (nothing is offered)
 *  - no-milestones: by milestones, no fundable milestone (nothing bills)
 *  - missing-amount: by milestones, a milestone with no amount (unknown, not a zero)
 * Not under a fixed fee above: that one bills instead.
 *
 * @return {{code: String, milestoneIds?: String[]}[]}
 */
export function getPackageBillingWarnings({ action, allActions }) {
    const type = action?.billingType

    if (!isFixedFee(type) || getBillingLock({ actionId: action._id, allActions })) return []

    if (type === BILLING.FIXED_PERCENT) {
        return isSetAmount(action.estRevenue) ? [] : [{ code: "no-fee" }]
    }

    const milestones = getPackageMilestones({ actionId: action._id, allActions })

    if (!milestones.length) return [{ code: "no-milestones" }]

    const missing = milestones.filter((ms) => !isSetAmount(ms.billRate))

    return missing.length ? [{ code: "missing-amount", milestoneIds: missing.map((ms) => ms._id) }] : []
}

/**
 * The models whose revenue isn't typed on the package: cost plus prices it from the work, a
 * fixed fee by milestones is the sum of its milestones. A revenue typed
 * on such a package is deleted. API twin: action.billing.js#clearPricedRevenue.
 */
export const isRevenueCalculated = (type) => type === BILLING.COST_PLUS || type === BILLING.FIXED_MILESTONES

/** What becomes of the fundable milestones when the model stops billing them. */
export const MILESTONES_CHOICE = {
    /** Regular milestones: dates stay, amounts stop billing */
    REGULAR: "regular",
    /** Fundable, billing on top of the work (time & materials, cost plus) */
    BILLABLE: "billable",
    DELETE: "delete",
}

/**
 * Everything a change of billing type does (the gear menu's Billing card and mAX's
 * set_package_billing), from one rule set:
 *  - change: what's written on the package. A revenue typed on a package whose new model
 *    calculates it is deleted; a fee by % complete moves into the milestone created for a
 *    lump sum with none, and a lump sum's milestones' sum becomes the fee by % complete.
 *  - create: the fundable milestone a lump sum with none gets on its end date (getFeeMilestone)
 *  - milestones: the fundable milestones whose meaning changes, and the choices the user is
 *    given (MILESTONES_CHOICE); `apply` says what to write for the choice made
 *  - cost plus starts at a 0% markup
 *
 * @param input.action {Object} The package as it is
 * @param input.billingType {String|null} null is no type of its own
 * @param input.allActions {Object[]}
 * @return {{change: Object, create: (Object|null), milestones: (null|{list: Object[], choices: String[],
 *   apply: Function})}}
 */
export function getBillingTypeChange({ action, billingType, allActions = [] }) {
    const from = action?.billingType || null
    const to = billingType || null
    const change = { billingType: to }
    const milestones = getPackageMilestones({ actionId: action?._id, allActions })
    const feeAbove = Boolean(getBillingLock({ actionId: action?._id, allActions }))

    // A lump sum with no milestones gets one, worth the revenue typed on the package (an
    // override, or the fee by % complete) so nothing is lost
    const create =
        to === BILLING.FIXED_MILESTONES && !milestones.length && !feeAbove
            ? getFeeMilestone({ action: { ...action, billingType: to }, allActions, fee: action?.estRevenue })
            : null

    if (to === BILLING.FIXED_PERCENT) {
        // The fee: what was typed (an override), or the lump sum's milestones' sum
        if (from === BILLING.FIXED_MILESTONES) change.estRevenue = getPackageFee({ action, allActions })
    } else if (isRevenueCalculated(to) || (from && isFixedFee(from))) {
        // Calculated by the new model, or a fee that means nothing outside its model
        if (isSetAmount(action?.estRevenue)) change.estRevenue = null
    }

    // An actual typed by hand only counts with no model at all
    if (to && isSetAmount(action?.actualRevenue)) change.actualRevenue = null

    if (to === BILLING.COST_PLUS && !Number.isFinite(action?.billingMarkup)) change.billingMarkup = 0

    // The fundable milestones stop being the fee (to % complete they don't bill; from a lump
    // sum to the work's models they'd bill on top of it): the user decides what they become
    let choices = null

    if (milestones.length && to === BILLING.FIXED_PERCENT) {
        choices = [MILESTONES_CHOICE.REGULAR, MILESTONES_CHOICE.DELETE]
    } else if (milestones.length && from === BILLING.FIXED_MILESTONES && to === BILLING.COST_PLUS) {
        choices = [MILESTONES_CHOICE.BILLABLE, MILESTONES_CHOICE.REGULAR, MILESTONES_CHOICE.DELETE]
    }

    return {
        change,
        create,
        milestones: choices
            ? {
                  list: milestones,
                  choices,
                  apply: (choice) =>
                      choice === MILESTONES_CHOICE.BILLABLE
                          ? []
                          : milestones.map((ms) =>
                                choice === MILESTONES_CHOICE.DELETE
                                    ? { milestone: ms, remove: true }
                                    : { milestone: ms, change: { pinType: null } },
                            ),
              }
            : null,
    }
}

/**
 * A fixed fee by milestones with no fundable milestone in it gets one on the package's end
 * date, made as the package menu's "Add a deliverable Milestone" makes one (create), then
 * made fundable (update) with the amount given, if any. Also the card's "Add a milestone".
 *
 * @param input.action {Object} The package
 * @param [input.fee] {Number|null} The milestone's amount
 * @param [input.title] {String}
 * @return {{create: Object, update: Object}} actionItemCreateX data, then actionItemUpdateX change
 */
export function getFeeMilestone({ action, fee = null, title }) {
    const end = toZonedTime(action.endDate || Date.now())

    return {
        create: {
            title: title || `${action.title || "Package"}: fee`,
            metaType: MILESTONE_META_TYPE,
            startDate: getTimeUtc(startOfDay(end)),
            endDate: getTimeUtc(endOfDay(end)),
            estimatedDuration: 8,
            sortIndex: (action.actionItems || []).length,
        },
        update: {
            pinType: "fundable-milestone",
            ...(isSetAmount(fee) ? { billRate: num(fee) } : {}),
        },
    }
}

/**
 * The invoice lines of an org's invoices (the wizard's), flat and kinded like the API's
 * /missions/:id/invoicedLines, for getInvoicedLinesFor.
 */
export function flattenInvoiceLines(invoices = []) {
    const kinds = [
        ["actionItems", "time"],
        ["fixedItems", "fixed"],
        ["scheduledPayments", "scheduled"],
        ["expenses", "expense"],
    ]

    return invoices.flatMap((inv) =>
        kinds.flatMap(([key, kind]) => (inv?.[key] || []).map((line) => ({ ...line, kind, invoiceId: inv._id }))),
    )
}

/** A % complete row's invoice itemId starts with this: one id per confirmation billed. */
export const progressItemPrefix = (actionId) => `progress:${actionId}:`

/** The wizard's itemId for billing a package's latest confirmed %. */
export function getProgressItemId({ actionId, progress }) {
    const ordered = orderProgress(progress)
    const last = ordered[ordered.length - 1]

    return `${progressItemPrefix(actionId)}${last ? last.at || last.asOf : 0}`
}

/**
 * Which invoiced lines (the API's /missions/:id/invoicedLines) bill the given tasks: time on
 * them (a time line's itemId is its timesheet's id + the task's), the cost lines linked to
 * them, the milestones among them, and the % complete rows of the given packages.
 */
export function getInvoicedLinesFor({ tasks = [], packageIds = [], planItems = [], lines = [] }) {
    const taskIds = new Set(tasks.map((ai) => ai?._id).filter(Boolean))
    const costLineIds = new Set(
        planItems.filter((p) => p?.type === "cost" && taskIds.has(p.linkedToTask)).map((p) => p._id),
    )
    const prefixes = packageIds.map(progressItemPrefix)

    return lines.filter((line) => {
        const id = line?.itemId || ""

        if (line?.kind === "time") return [...taskIds].some((taskId) => id.endsWith(taskId))
        if (line?.kind === "fixed") return costLineIds.has(id)
        if (line?.kind === "scheduled") return taskIds.has(id) || prefixes.some((p) => id.startsWith(p))

        return false
    })
}

/**
 * The packages that decide how their work bills, each with the tasks it covers: a task belongs
 * to the package its billing comes from (getTaskBilling). Not set work isn't listed.
 *
 * @return {{action: Object, billing: Object, tasks: Object[]}[]} In the order of allActions
 */
export function getBillingOwners({ allActions = [] }) {
    const owners = new Map()

    allActions.forEach((pkg) => {
        ;(pkg?.actionItems || []).forEach((ai) => {
            const billing = getPackageBilling({ actionId: pkg._id, allActions })

            if (!billing) return
            if (!owners.has(billing.packageId)) owners.set(billing.packageId, { billing, tasks: [] })

            owners.get(billing.packageId).tasks.push(ai)
        })
    })

    return allActions
        .filter((a) => a && isBillingType(a.billingType) && !getBillingLock({ actionId: a._id, allActions }))
        .map((action) => ({
            action,
            billing: owners.get(action._id)?.billing || describeBilling(action, allActions),
            tasks: owners.get(action._id)?.tasks || [],
        }))
}
